import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { auth, onServerWaking } from "@/lib/api";

const PLAN_LABELS = { free: "Free", team: "Team", business: "Business" };

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", tos: false });
  const [orgName, setOrgName] = useState("");
  const [loading, setLoading] = useState(false);
  const [waking, setWaking] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const [searchParams] = useSearchParams();
  const plan = PLAN_LABELS[searchParams.get("plan")] ? searchParams.get("plan") : "free";

  useEffect(() => onServerWaking(setWaking), []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password !== form.confirm) return setErr("Passwords do not match.");
    if (!form.tos) return setErr("Please accept the Terms of Service.");
    if (!orgName.trim()) return setErr("Enter your organization's name.");
    setLoading(true);
    try {
      const res = await auth.register({
        name: form.name, email: form.email, password: form.password, tos_accepted: form.tos,
        org_name: orgName, plan,
      });
      setDone(res);
    } catch (e2) {
      setErr(
        e2?.response?.data?.detail ||
          (e2?.response
            ? "Registration failed"
            : "Couldn't reach the server — it may be waking up. Please wait a moment and try again."),
      );
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    const pending = done.pending_approval;
    return (
      <div className="login-shell" data-testid="register-done">
        <div className="login-card">
          <div className="login-logo"><span className="logo-icon">⬡</span><span className="app-name">GovCo<span>.AI</span></span></div>
          <h2>{pending ? "Request sent" : "Check your email"}</h2>
          {pending ? (
            <p className="sub" data-testid="pending-msg">
              We noticed your company already uses GovCo.AI, so we sent your request to join to their
              administrator. You'll be able to sign in once it's <strong>approved</strong>.
            </p>
          ) : (
            <p className="sub">Your organization <strong>{orgName}</strong> is ready. We sent a verification link to <strong>{form.email}</strong>.</p>
          )}
          {done.verify_link && (
            <div className="alert alert-info" style={{ marginTop: 12, wordBreak: "break-all" }}>
              <span>🔗</span>
              <span>Dev mode — verify directly: <a href={done.verify_link}>{done.verify_link}</a></span>
            </div>
          )}
          <Link className="btn btn-primary w-full" style={{ justifyContent: "center", marginTop: 18 }} to="/login">Go to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell" data-testid="register-page">
      <form className="login-card" onSubmit={submit}>
        <div className="login-logo"><span className="logo-icon">⬡</span><span className="app-name">GovCo<span>.AI</span></span></div>
        <h2>Create your account</h2>
        <p className="sub">AI-powered Government Solicitation Response Platform</p>
        <div className="alert alert-info" style={{ marginBottom: 12, fontSize: 12.5 }} data-testid="reg-plan-badge">
          <span>
            If your company isn't already on GovCo.AI, we'll create it on the <strong>{PLAN_LABELS[plan]}</strong> plan.{" "}
            <Link to="/pricing" style={{ color: "var(--primary)", fontWeight: 600 }}>Change plan</Link>
          </span>
        </div>

        <label htmlFor="orgname">Organization</label>
        <input
          id="orgname" placeholder="Organization name (e.g. Acme Federal LLC)"
          value={orgName} onChange={(e) => setOrgName(e.target.value)} data-testid="reg-orgname"
        />
        <div style={{ fontSize: 11, color: "var(--text-muted)", margin: "4px 0 6px" }}>
          If we recognize your company from your work email, you'll join it instead — pending approval
          from their administrator. Otherwise this name is used to create it, and you'll be its
          administrator.
        </div>

        <label htmlFor="name">Full name</label>
        <input id="name" value={form.name} onChange={set("name")} required data-testid="reg-name" />
        <label htmlFor="email">Work email</label>
        <input id="email" type="email" value={form.email} onChange={set("email")} required autoComplete="email" data-testid="reg-email" />
        <label htmlFor="password">Password</label>
        <input id="password" type="password" value={form.password} onChange={set("password")} required autoComplete="new-password" data-testid="reg-password" />
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
          Min 12 chars with upper, lower, number, and special character.
        </div>
        <label htmlFor="confirm">Confirm password</label>
        <input id="confirm" type="password" value={form.confirm} onChange={set("confirm")} required autoComplete="new-password" data-testid="reg-confirm" />

        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 10, fontWeight: 400 }}>
          <input type="checkbox" checked={form.tos} onChange={(e) => setForm({ ...form, tos: e.target.checked })} data-testid="reg-tos" style={{ width: "auto", marginTop: 3 }} />
          <span style={{ fontSize: 12 }}>
            I agree to the{" "}
            <Link to="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "var(--primary)", fontWeight: 600 }}>Terms of Service</Link>
            {" "}and{" "}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "var(--primary)", fontWeight: 600 }}>Privacy Policy</Link>.
          </span>
        </label>

        {err && <div className="err" data-testid="register-error">{err}</div>}
        {loading && waking && (
          <div className="sub" style={{ marginTop: 10, fontSize: 12 }} data-testid="register-waking">
            ⏳ Waking up the server (free tier sleeps when idle) — this can take up to a minute…
          </div>
        )}

        <button type="submit" className="btn btn-primary w-full" style={{ justifyContent: "center", marginTop: 16 }} disabled={loading} data-testid="register-submit">
          {loading ? <span className="spinner white" /> : "Create account"}
        </button>
        <div style={{ textAlign: "center", marginTop: 14, fontSize: 13 }}>
          Already have an account? <Link to="/login" style={{ color: "var(--primary)", fontWeight: 600 }}>Sign in</Link>
        </div>
      </form>
    </div>
  );
}
